import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DateFieldProps as AriaDateFieldProps,
  DatePicker as AriaDatePicker,
  ValidationResult,
  DatePickerProps as AriaDatePickerProps,
} from 'react-aria-components';
import { type DateValue, getLocalTimeZone, parseDate } from '@internationalized/date';
import { VariantProps } from 'tailwind-variants';

import { composeTailwindRenderProps } from '../../lib/styles';
import {
  DATE_INPUT_CALENDAR_ICON_CLASSNAME,
  DATE_INPUT_CALENDAR_POPOVER_CLASSNAME,
  DateInputInner,
} from './DateInputInner';
import { fieldStyles } from '../Field';
import { isDateInRange, parseToDateValue } from './DateInput.utils';
import { formatRequestDate } from '@page/core/utils/date';
import { PopoverContentProps } from '../Popover';

export interface DateFieldProps<T extends DateValue> extends AriaDateFieldProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
}

interface BaseDateInputProps
  extends Omit<AriaDatePickerProps<DateValue>, 'value' | 'onChange'>,
    VariantProps<typeof fieldStyles> {
  label?: string;
  description?: string;
  error?: string;

  min?: string | null;
  max?: string | null;

  disabled?: boolean;
  clearable?: boolean;
  reserveErrorSpace?: boolean;

  /**
   * Commit valid dates to `onChange` as soon as they change, instead of waiting for blur.
   * Needed when the parent saves on outside click, which fires before the blur commit.
   */
  commitOnChange?: boolean;

  fieldClassName?: string;
  labelClassName?: string;
  inputClassName?: string;

  popoverContentProps?: PopoverContentProps;
}

type OnChangeContext = {
  /**
   * The original blur event that triggered the change, useful for validation that needs to run on blur.
   */
  blurEvent?: React.FocusEvent;
};

interface DateInputPropsWithDate extends BaseDateInputProps {
  value?: Date | null;
  onChange?: (date: Date | null, context?: OnChangeContext) => void;
}

interface DateInputPropsWithString extends BaseDateInputProps {
  value?: string | null;
  onChange?: (date: string | null, context?: OnChangeContext) => void;
}

export type DateInputProps = DateInputPropsWithDate | DateInputPropsWithString;

// Function overloads for better type inference
export function DateInput(props: DateInputPropsWithDate): React.ReactElement;
export function DateInput(props: DateInputPropsWithString): React.ReactElement;
export function DateInput({
  value,
  onChange,

  label,
  name,
  description,
  error,

  min = '1920-01-01',
  max,
  minValue: minValueProp,
  maxValue: maxValueProp,

  disabled,
  clearable,
  reserveErrorSpace,
  commitOnChange,

  size,
  variant,

  className,
  labelClassName,
  fieldClassName,
  inputClassName,

  popoverContentProps,

  onBlur,

  onFocus,
  ...rest
}: DateInputProps) {
  const [localValue, setLocalValue] = useState<DateValue | null>(() => parseToDateValue(value));
  const [blurError, setBlurError] = useState<string | null>(null);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (isFocusedRef.current) return;

    setLocalValue(parseToDateValue(value));
  }, [value]);

  const minValue = useMemo(() => {
    if (minValueProp) return minValueProp;

    if (min) return parseDate(min);

    return undefined;
  }, [min, minValueProp]);

  const maxValue = useMemo(() => {
    if (maxValueProp) return maxValueProp;

    if (max) return parseDate(max);

    return undefined;
  }, [max, maxValueProp]);

  const handleChange = (date: DateValue | null) => {
    setLocalValue(date);

    if (blurError && date && isDateInRange(date, minValue, maxValue)) {
      setBlurError(null);
    }

    if (commitOnChange && (!date || isDateInRange(date, minValue, maxValue))) {
      commitChange(date);
    }
  };

  const commitChange = (dateValue: DateValue | null, context?: OnChangeContext) => {
    if (!onChange) return;

    if (!dateValue) {
      onChange?.(null, context);

      return;
    }

    const newDateObject = dateValue.toDate(getLocalTimeZone());

    if (value instanceof Date) {
      (onChange as (date: Date | null, context?: OnChangeContext) => void)(newDateObject, context);
    } else {
      (onChange as (date: string | null, context?: OnChangeContext) => void)(formatRequestDate(newDateObject), context);
    }
  };

  const handleClear = () => {
    setLocalValue(null);
    commitChange(null);
  };

  const handleBlur = (event: React.FocusEvent, errors: string[]) => {
    const isCalendarRelated = event?.relatedTarget?.closest(
      `.${DATE_INPUT_CALENDAR_ICON_CLASSNAME}, .${DATE_INPUT_CALENDAR_POPOVER_CLASSNAME}`,
    );

    if (errors.length) {
      setBlurError(errors[0]);
      setLocalValue(parseToDateValue(value));
    } else {
      setBlurError(null);
      if (!isCalendarRelated) {
        commitChange(localValue, { blurEvent: event });
      }
    }

    if (!isCalendarRelated) {
      isFocusedRef.current = false;
    }

    onBlur?.(event);
  };

  const handleFocus = (event: React.FocusEvent) => {
    isFocusedRef.current = true;
    onFocus?.(event);
  };

  return (
    <AriaDatePicker
      value={localValue}
      className={composeTailwindRenderProps(className, 'group flex flex-col outline-none')}
      aria-label={label || 'Date input'}
      minValue={minValue}
      maxValue={maxValue}
      onChange={handleChange}
      shouldForceLeadingZeros
      onFocus={handleFocus}
      {...rest}
    >
      <DateInputInner
        label={label}
        name={name}
        error={blurError ?? error}
        disabled={disabled}
        clearable={clearable}
        reserveErrorSpace={reserveErrorSpace}
        size={size}
        variant={variant}
        className={fieldClassName}
        labelClassName={labelClassName}
        inputClassName={inputClassName}
        popoverContentProps={popoverContentProps}
        onBlur={handleBlur}
        onClear={handleClear}
      />
    </AriaDatePicker>
  );
}
